import React, { useEffect, useState } from 'react';
import EditableSection from './EditableSection';
import CtaTable, { type CtaEntry } from './CtaTable';

export interface ScrapedPage {
  url: string;
  sourceType: string;
  title: string;
  metaDescription: string;
  extractedText: string;
  scrapedAt?: string;
  faviconUrl?: string;
  logoUrl?: string;
  titleFontFamily?: string;
  titleColor?: string;
}

export const PROFILE_SECTION_DEFINITIONS = [
  { key: 'agent_name', title: 'Name der Agentin', placeholder: 'Name der Agentin festlegen...', rows: 2 },
  { key: 'company_name', title: 'Unternehmensname', placeholder: 'Unternehmensname eintragen...', rows: 2 },
  { key: 'founding_year_and_location', title: 'Gründungsjahr & Gründungsort', placeholder: 'Gründungsdaten erfassen...', rows: 2 },
  { key: 'founders', title: 'Gründer / Gründungsteam', placeholder: 'Gründer oder Kernteam beschreiben...', rows: 3 },
  { key: 'contact_details', title: 'Adresse & Kontaktdaten', placeholder: 'Adresse, Mail, Telefonnummer, WhatsApp oder Kontaktwege ergänzen...', rows: 3 },
  { key: 'industry', title: 'Branche', placeholder: 'Branche und Marktumfeld ergänzen...', rows: 2 },
  { key: 'mission_vision_values', title: 'Mission, Vision & Werte', placeholder: 'Mission, Vision und Werte notieren...', rows: 4 },
  { key: 'positioning_core_message', title: 'Positionierung / Kernbotschaft', placeholder: 'Positionierung und Kernbotschaft festhalten...', rows: 3 },
  { key: 'achievements_milestones', title: 'Erfolge & Meilensteine', placeholder: 'Relevante Meilensteine und Nachweise ergänzen...', rows: 3 },
  { key: 'customers_target_groups', title: 'Kunden / Zielgruppen', placeholder: 'Typische Kunden und Zielgruppen beschreiben...', rows: 3 },
  { key: 'calls_to_action', title: 'Calls to Action', placeholder: 'Wiederkehrende CTAs erfassen...', rows: 2 },
  { key: 'products_services', title: 'Produkte / Angebote / Leistungen', placeholder: 'Produkte und Leistungen erfassen...', rows: 4 },
  { key: 'address_formality', title: 'Anrede Du vs. Sie', placeholder: 'Anredeform dokumentieren...', rows: 2 },
  { key: 'language_style', title: 'Sprachstil', placeholder: 'Ton, Stil und Ausdruck beschreiben...', rows: 2 },
  { key: 'emoji_usage', title: 'Emoji-Verwendung', placeholder: 'Emoji-Nutzung festhalten...', rows: 2 },
  { key: 'recurring_phrases', title: 'Verwendete Formulierungen', placeholder: 'Wiederkehrende Formulierungen sammeln...', rows: 3 },
  { key: 'landing_page_excerpt', title: 'Textausschnitt der Landing-Page', placeholder: 'Hero-/Landing-Page-Ausschnitt festhalten...', rows: 5 },
  { key: 'color_codes', title: 'Farbcodes', placeholder: 'Markenfarben / Farbcodes ergänzen...', rows: 2 },
] as const;

export type BackendProfileSectionKey = typeof PROFILE_SECTION_DEFINITIONS[number]['key'];
export type BackendProfileSections = Record<BackendProfileSectionKey, string>;

interface CustomerProfilePanelProps {
  workspaceSlug: string;
  website: string;
  productWebsite: string;
  lastScrapedAt?: string | null;
  initialProfileSections?: BackendProfileSections | null;
  scrapedPages: ScrapedPage[];
  isLoading: boolean;
  scrapedError: string | null;
  onSaveSections: (sections: BackendProfileSections) => Promise<void>;
  ctas?: CtaEntry[];
  onSaveCtas?: (ctas: CtaEntry[]) => Promise<void>;
  appearanceMode?: 'default' | 'brand';
  onChangeAppearanceMode?: (mode: 'default' | 'brand') => void;
  isDarkMode: boolean;
}

const emptyBackendProfileSections = (): BackendProfileSections => (
  Object.fromEntries(PROFILE_SECTION_DEFINITIONS.map((section) => [section.key, ''])) as BackendProfileSections
);

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return 'Noch kein Scrape-Durchlauf';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('de-DE');
};

const CustomerProfilePanel: React.FC<CustomerProfilePanelProps> = ({
  workspaceSlug,
  website,
  productWebsite,
  lastScrapedAt,
  initialProfileSections,
  scrapedPages,
  isLoading,
  scrapedError,
  onSaveSections,
  ctas,
  onSaveCtas,
  appearanceMode = 'default',
  onChangeAppearanceMode,
  isDarkMode,
}) => {
  const [sections, setSections] = useState<BackendProfileSections>(() => (
    initialProfileSections ?? emptyBackendProfileSections()
  ));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    setSections(initialProfileSections ?? emptyBackendProfileSections());
    setSaveSuccessMessage(null);
  }, [initialProfileSections, scrapedPages, website, productWebsite, workspaceSlug]);

  const handleSectionChange = (key: BackendProfileSectionKey, value: string): void => {
    setSaveError(null);
    setSaveSuccessMessage(null);
    setSections((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccessMessage(null);
    try {
      await onSaveSections(sections);
      setSaveSuccessMessage('Profil gespeichert.');
    } catch {
      setSaveError('Profil konnte nicht gespeichert werden.');
    } finally {
      setIsSaving(false);
    }
  };

  const cardClasses = isDarkMode
    ? 'border border-white/10 bg-slate-900/90 text-slate-50'
    : 'border border-orange-100 bg-white/92 text-slate-900';
  const labelClasses = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const valueClasses = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const loadingRowClasses = isDarkMode ? 'bg-slate-800/70 text-slate-200' : 'bg-orange-50 text-slate-700';
  const buttonClasses = isDarkMode
    ? 'bg-emerald-500 text-white hover:bg-emerald-400 disabled:bg-slate-700 disabled:text-slate-300'
    : 'bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-500';

  return (
    <section className={`rounded-3xl p-6 shadow-[0_18px_45px_rgba(15,23,42,0.18)] ${cardClasses}`}>
      <h2 className="text-xl font-semibold tracking-tight">Kundenprofil</h2>
      <dl className={`mt-5 grid gap-3 rounded-2xl border px-4 py-3 text-sm ${
        isDarkMode ? 'border-white/10 bg-slate-950/45' : 'border-orange-100 bg-orange-50/60'
      }`}>
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
          <div className="min-w-0">
            <dt className={`font-medium ${labelClasses}`}>Workspace</dt>
            <dd className={`mt-0.5 truncate font-semibold ${valueClasses}`}>{workspaceSlug}</dd>
          </div>
          <div className="min-w-0 sm:text-right">
            <dt className={`font-medium ${labelClasses}`}>Zuletzt gescrapt</dt>
            <dd className={`mt-0.5 ${valueClasses}`}>{formatDateTime(lastScrapedAt)}</dd>
          </div>
        </div>
        <div className="min-w-0">
          <dt className={`font-medium ${labelClasses}`}>Website</dt>
          <dd className={`mt-0.5 break-all ${valueClasses}`}>{website || 'Noch nicht hinterlegt'}</dd>
        </div>
        {productWebsite ? (
          <div className="min-w-0">
            <dt className={`font-medium ${labelClasses}`}>Produkt-Website</dt>
            <dd className={`mt-0.5 break-all ${valueClasses}`}>{productWebsite}</dd>
          </div>
        ) : null}
      </dl>

      {onChangeAppearanceMode ? (
        <div className={`mt-5 rounded-2xl border px-4 py-3 ${
          isDarkMode ? 'border-white/10 bg-slate-950/45' : 'border-orange-100 bg-orange-50/60'
        }`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className={`text-sm font-semibold ${valueClasses}`}>Hintergrund</h3>
              <p className={`mt-1 text-sm ${labelClasses}`}>
                Verwende die extrahierten Markenfarben oder den Standard-Look.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onChangeAppearanceMode('brand')}
                className={`rounded-xl px-3 py-2 text-sm font-medium transition ${
                  appearanceMode === 'brand'
                    ? isDarkMode
                      ? 'bg-emerald-500 text-white'
                      : 'bg-emerald-600 text-white'
                    : isDarkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                Markenlook
              </button>
              <button
                type="button"
                onClick={() => onChangeAppearanceMode('default')}
                className={`rounded-xl px-3 py-2 text-sm font-medium transition ${
                  appearanceMode === 'default'
                    ? isDarkMode
                      ? 'bg-slate-200 text-slate-900'
                      : 'bg-slate-900 text-white'
                    : isDarkMode
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                Default
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {scrapedError ? (
        <div className={`mt-5 rounded-2xl border px-4 py-3 text-sm ${
          isDarkMode ? 'border-rose-400/40 bg-rose-500/10 text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-700'
        }`}>
          {scrapedError}
        </div>
      ) : null}

      {isLoading ? (
        <div className={`mt-6 rounded-2xl px-4 py-3 text-sm ${loadingRowClasses}`}>
          Gescrapte Inhalte werden geladen...
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div className="space-y-5">
            {PROFILE_SECTION_DEFINITIONS.map((section) => (
              <EditableSection
                key={section.key}
                id={`profile-section-${section.key}`}
                title={section.title}
                value={sections[section.key]}
                placeholder={section.placeholder}
                rows={section.rows}
                onChange={(value) => handleSectionChange(section.key, value)}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>

          {onSaveCtas ? (
            <CtaTable
              initialCtas={ctas ?? []}
              onSave={onSaveCtas}
              isDarkMode={isDarkMode}
            />
          ) : null}

          {saveError ? (
            <div className={`rounded-2xl border px-4 py-3 text-sm ${
              isDarkMode ? 'border-rose-400/40 bg-rose-500/10 text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-700'
            }`}>
              {saveError}
            </div>
          ) : null}

          {saveSuccessMessage ? (
            <div className={`rounded-2xl border px-4 py-3 text-sm ${
              isDarkMode ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}>
              {saveSuccessMessage}
            </div>
          ) : null}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => { void handleSave(); }}
              disabled={isSaving}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${buttonClasses}`}
            >
              {isSaving ? 'Speichert...' : 'Profil speichern'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CustomerProfilePanel;
